import { DialogM } from "@common/components/alert/dialog/Dialog"
import { FormStepper } from "@common/components/stepper/FormStepper"
import LinkTextM from "@common/components/text/LinkTextM"
import TitleM from "@common/components/text/TitleM"
import { useApiStatus } from "@common/hooks/useApiStatus"
import { congratsTypes } from "@common/screens/CongratsScreen"
import { ScreenSkeleton } from "@common/screens/ScreenSkeleton"
import { theme } from "@common/theme/theme"
import { apiIdle, apiRequest } from "@data/redux/actions/api"
import { authService } from "@data/services/authService"
import { liveService } from "@data/services/liveService"
import { findItemInListWithId } from "@data/utility/dataUtils"
import { supportsNoShowRecovery } from "@data/utility/liveSessionType"
import { capitalize, plr, readableDayTime } from "@data/utility/Utils"
import { validation } from "@data/utility/validation"
import { useNavigation } from "@react-navigation/native"
import moment from "moment"
import React, { useContext, useEffect, useState } from "react"
import { Linking, View } from "react-native"
import { Checkbox } from "react-native-paper"
import { connect } from "react-redux"
import TextM from "../../common/components/text/TextM"
import { DimensionsContext } from "../../common/utils/mobileUtils"
import { findClosestNextOccurrence } from "../../student/account/onboarding/LiveRoomCard"
import MoveSessionReasonComponent, {
    delayOptions,
    importantWarning,
} from "./MoveSessionReasonComponent"
import AbsenceImpactComponent from "./AbsenceImpactComponent"
import {
    emptiesSession,
    hasTutorChange,
    tutorChangeIds,
    tutorChangeNotice,
} from "./absenceImpact"
import SelectDelayComponent from "./SelectDelayComponent"
import SelectSituationScreen from "./SelectSituationComponent"
import TutorLiveCardComponent from "./TutorLiveCardComponent"

function ManageRDVScreen(props) {
    const { route } = props
    const { dateTime = null } = route.params
    // Ouvert depuis son URL (web : rechargement, lien), l'identifiant arrive en
    // texte : comparé tel quel aux lives, la séance concernée disparaissait.
    const liveId = Number(route.params.liveId)
    const [formValues, setFormValues] = useState(null)
    const [absenceSummary, setAbsenceSummary] = useState(null)
    // Élève par élève, ce que l'absence fera : lu avant confirmation, et renvoyé
    // à la déclaration pour que le backend n'applique que ce qui a été montré.
    const [absenceImpact, setAbsenceImpact] = useState(null)
    const [acceptsLiveLoss, setAcceptsLiveLoss] = useState(false)
    const [dynamicAbsenceDays, setDynamicAbsenceDays] = useState(0)
    const navigation = useNavigation()
    const dimensions = useContext(DimensionsContext)
    const cardWidth = dimensions.width - 40
    const defaultValues = {}

    // Fetch absence summary when component mounts + reset stale error state
    useEffect(() => {
        props.apiIdle(liveService.reportAbsence)
        props.apiRequest(liveService.getSubstitutionAbsenceSummary)
        // Un individuel ne propose que le déplacement : pas d'absence, pas d'impact.
        if (!isIndividual) {
            props.apiRequest(liveService.getTutorAbsenceImpact, { id: liveId })
        }
    }, [])

    useApiStatus(liveService.getTutorAbsenceImpact, false, false, (data) => {
        setAbsenceImpact(data?.students ?? [])
    })

    useApiStatus(
        liveService.getSubstitutionAbsenceSummary,
        false,
        false,
        (data) => {
            const summary = data
            if (summary) {
                // Calculate exceeds_limit and remaining_days in frontend
                const exceedsLimit = summary.total_days >= summary.limit
                const remainingDays = Math.max(
                    0,
                    summary.limit - summary.total_days
                )
                setAbsenceSummary({
                    ...summary,
                    exceeds_limit: exceedsLimit,
                    remaining_days: remainingDays,
                })

                // E1 pre-check: show hypercare warning upfront
                if (summary.hypercare_block?.has_hypercare_students) {
                    DialogM.show({
                        text1: "Ta présence compte",
                        text2: summary.hypercare_block.message,
                        buttonText1: "Contacter sur WhatsApp",
                        onPress: () => {
                            const url = summary.hypercare_block.support_whatsapp_url || props.supportWhatsappUrl
                            if (url) {
                                Linking.openURL(url).catch(() => {})
                            }
                        },
                        buttonText2: "J'ai compris",
                        onPressBack: () => {},
                    })
                }
            }
        }
    )

    const liveSession = findItemInListWithId(liveId, props.user?.live_sessions)
    const closestNextOccurrence = findClosestNextOccurrence([liveSession])
    const liveMoment = closestNextOccurrence?.date
        ? moment(closestNextOccurrence?.date)
        : null

    // State for tracking next month's dynamic days
    const [dynamicNextMonthDays, setDynamicNextMonthDays] = useState(0)

    // Calculate dynamic absence days based on selected occurrences
    useEffect(() => {
        if (!absenceSummary) {
            setDynamicAbsenceDays(0)
            setDynamicNextMonthDays(0)
            return
        }

        const existingDates = Object.keys(absenceSummary.dates || {})
        const existingNextMonthDates = Object.keys(
            absenceSummary.next_month?.dates || {}
        )

        // Get current month and next month boundaries
        const currentMonthEnd = moment().endOf("month")
        const nextMonthStart = moment().add(1, "month").startOf("month")
        const nextMonthEnd = moment().add(1, "month").endOf("month")

        // Collect unique dates separated by month
        const selectedCurrentMonthDates = new Set()
        const selectedNextMonthDates = new Set()

        // Add dates from selected other occurrences
        if (formValues?.other_occurrences) {
            formValues.other_occurrences.forEach((occKey) => {
                // Parse the key format: "YYYY-MM-DD HH:mm_liveSessionId"
                const occurrenceDate = occKey.split("_")[0]
                const occMoment = moment(occurrenceDate)
                const occDate = occMoment.format("YYYY-MM-DD")

                if (occMoment.isSameOrBefore(currentMonthEnd, "day")) {
                    // Current month
                    if (!existingDates.includes(occDate)) {
                        selectedCurrentMonthDates.add(occDate)
                    }
                } else if (
                    occMoment.isBetween(
                        nextMonthStart,
                        nextMonthEnd,
                        "day",
                        "[]"
                    )
                ) {
                    // Next month
                    if (!existingNextMonthDates.includes(occDate)) {
                        selectedNextMonthDates.add(occDate)
                    }
                }
            })
        }

        setDynamicAbsenceDays(selectedCurrentMonthDates.size)
        setDynamicNextMonthDays(selectedNextMonthDates.size)
    }, [formValues?.other_occurrences, absenceSummary, dateTime, liveMoment])


    const isIndividual = liveSession?.live_room?.type === "individual"

    // Build list of all occurrences for next 7 days starting from absence date
    const buildSessionsList = (liveSessions, absenceDate, absenceSession) => {
        const occurrences = []
        const now = moment()
        const endDate = moment(absenceDate).add(7, "days")
        const absenceMoment = moment(absenceDate)
        const minutesUntilAbsence = absenceMoment.diff(now, "minutes")

        if (minutesUntilAbsence < 30) {
            return []
        }

        // Get the list of students from the absence session
        const absentStudentIds =
            absenceSession?.students?.map((student) => student.id) || []

        liveSessions.forEach((liveSession) => {
            // Only include no-show-recoverable group sessions as makeup options
            if (!supportsNoShowRecovery(liveSession.live_room)) {
                return
            }

            // Get all next_occurrences for this live session
            const sessionOccurrences = liveSession.next_occurrences || []

            sessionOccurrences.forEach((occurrence) => {
                const occurrenceDate = moment(occurrence.date)

                // Exclude the specific occurrence we're declaring absence for
                if (
                    liveSession.id === absenceSession?.id &&
                    occurrence.date === absenceDate
                ) {
                    return
                }

                // Exclude occurrences that already have a substitution
                if (occurrence.substitution_id) {
                    return
                }

                // Exclude sessions that already have 3 or more students
                const sessionStudentCount = liveSession.students?.length || 0
                if (sessionStudentCount >= 3) {
                    return
                }

                // Exclude occurrences that contain ALL of the absent students
                const occurrenceStudentIds =
                    liveSession.students?.map((student) => student.id) || []

                const hasAllAbsentStudents = absentStudentIds.every(
                    (studentId) => occurrenceStudentIds.includes(studentId)
                )

                if (hasAllAbsentStudents) {
                    return
                }

                // Only show occurrences that are:
                // 1. After now (haven't occurred yet)
                // 2. Within 7 days from absence date
                if (
                    occurrenceDate.isAfter(now) &&
                    occurrenceDate.isBefore(endDate)
                ) {
                    occurrences.push({
                        liveSessionId: liveSession.id,
                        occurrenceDate: occurrence.date,
                        liveSession: liveSession,
                        occurrence: occurrence,
                    })
                }
            })
        })

        // Sort by date (earliest first)
        occurrences.sort((a, b) => {
            const dateA = moment(a.occurrenceDate)
            const dateB = moment(b.occurrenceDate)
            return dateA.isBefore(dateB) ? -1 : dateA.isAfter(dateB) ? 1 : 0
        })

        return occurrences
    }

    // Build otherOccurrencesList from all tutor's live sessions
    const buildOtherOccurrencesList = () => {
        if (!props.user?.live_sessions) return []

        const occurrences = []
        const now = moment()
        const processedSessionIds = new Set()

        // Get the currently selected session date
        const selectedOccurrenceDate =
            dateTime ?? liveMoment?.format("YYYY-MM-DD HH:mm")

        props.user.live_sessions.forEach((session) => {
            // Skip if we've already processed this session
            if (processedSessionIds.has(session.id)) {
                return
            }
            processedSessionIds.add(session.id)
            // Filter out individual and trial sessions
            if (["individual", "trial"].includes(session.live_room?.type)) {
                return
            }

            if (
                session.next_occurrences &&
                session.next_occurrences.length > 0
            ) {
                // Check if user is a substitute for any occurrence in this session
                const userIsSubstituteInSession = session.next_occurrences.some(
                    (occ) => occ.substitute_id === props.user?.id
                )

                session.next_occurrences.forEach((occurrence) => {
                    const isSubstituteForOccurrence =
                        occurrence.substitute_id === props.user?.id
                    const isSelectedOccurrence =
                        occurrence.date === selectedOccurrenceDate &&
                        session.id === liveId

                    // Skip if user is not the substitute for sessions/occurrences with substitutions
                    // — never the séance being declared: it is the one the step shows.
                    if (
                        (userIsSubstituteInSession ||
                            occurrence.substitution_id !== null) &&
                        !isSubstituteForOccurrence &&
                        !isSelectedOccurrence
                    ) {
                        return
                    }

                    const occurrenceMoment = moment(occurrence.date)

                    // Filter out occurrences that have already started or passed,
                    // EXCEPT the currently selected session
                    if (
                        occurrenceMoment.isSameOrBefore(now) &&
                        !isSelectedOccurrence
                    ) {
                        return
                    }

                    occurrences.push({
                        liveSessionId: session.id,
                        occurrenceDate: occurrence.date,
                        liveSession: session,
                        occurrence: occurrence,
                        studentsListString:
                            session.students_list_string === ""
                                ? "(aucun élève)"
                                : session.students_list_string,
                    })
                })
            }
        })

        // Sort by date (earliest first)
        return occurrences.sort((a, b) => {
            return moment(a.occurrenceDate).diff(moment(b.occurrenceDate))
        })
    }

    const otherOccurrencesList = buildOtherOccurrencesList()

    const processedMakeupOccurrences =
        formValues?.type === "absence"
            ? buildSessionsList(
                  props.user?.live_sessions || [],
                  liveMoment?.format("YYYY-MM-DD HH:mm"),
                  liveSession
              )
            : []

    // Determine if makeup sessions steps should be skipped
    // Skip if: other occurrences selected (besides the main absence) OR no makeup sessions available OR less than 30 minutes before session
    const currentAbsenceDate = liveMoment?.format("YYYY-MM-DD HH:mm")
    const otherOccurrencesWithoutMain =
        formValues?.other_occurrences?.filter(
            (date) => date?.split("_")[0] !== currentAbsenceDate
        ) || []
    const minutesUntilSession = liveMoment
        ? liveMoment.diff(moment(), "minutes")
        : 0
    const isTooCloseToSession = minutesUntilSession < 30
    // ...or when every student leaves: nobody is left to offer a makeup to.
    const shouldSkipMakeupSteps =
        otherOccurrencesWithoutMain.length > 0 ||
        processedMakeupOccurrences.length === 0 ||
        isTooCloseToSession ||
        emptiesSession(absenceImpact)

    const isReplacement = dateTime
        ? liveSession?.next_occurrences?.find((occ) => occ.date === dateTime)
              ?.substitution_id
        : closestNextOccurrence?.substitution_id
    const isAccepted = dateTime
        ? liveSession?.next_occurrences?.find((occ) => occ.date === dateTime)
              ?.substitute_id
        : closestNextOccurrence?.substitute_id

    const typeData = isIndividual
        ? [
              {
                  label: "Déplacer cette séance uniquement",
                  value: "one_occurrence",
              },
              {
                  label:
                      "Déplacer les séances " +
                      (readableDayTime(
                          liveSession?.day_time,
                          liveSession?.duration
                      )?.toLowerCase() || ""),
                  value: "all_occurrences",
              },
              {
                  label: "Autre",
                  value: "other",
              },
          ]
        : [
              {
                  label: "Je vais être en retard, c'est vraiment inévitable",
                  value: "delay",
                  description: !liveMoment?.isSame(moment(), "date")
                      ? "On ne peut signaler un retard que le jour-même"
                      : "",
                  disabled: !liveMoment?.isSame(moment(), "date"),
              },
              {
                  label: "Je dois m'absenter",
                  value: "absence",
                  description:
                      liveSession?.live_room?.type === "trial"
                          ? "Pour les séances d'essai, veuillez contacter le support"
                          : "",
                  disabled: liveSession?.live_room?.type === "trial",
              },
              ...(isReplacement && isAccepted
                  ? []
                  : [
                        {
                            // Every week, not only this one: a single date is « Je dois m'absenter ».
                            label:
                                "Je ne suis plus disponible le " +
                                (readableDayTime(
                                    liveSession?.day_time,
                                    liveSession?.duration
                                )?.toLowerCase() || "") +
                                ", toutes les semaines",
                            value: "unavailable",
                        },
                        {
                            label: "Autre",
                            value: "other",
                        },
                    ]),
          ]

    const genericDescription = (oneOccurrence = true) => (
        <View
            style={{
                width: "100%",
            }}
        >
            <TutorLiveCardComponent
                liveSession={liveSession}
                noButtons
                noAvatars
                oneOccurrence={oneOccurrence}
                dateTime={dateTime}
            />
        </View>
    )

    const formData = [
        {
            label: null,
            description: (
                <>
                    {genericDescription()}
                    <TitleM
                        style={{ marginTop: 10, marginBottom: 0 }}
                        fontWeight={"ExtraBold"}
                    >
                        {isIndividual
                            ? "Que souhaites-tu faire ?"
                            : "Décris ta situation"}
                    </TitleM>
                </>
            ),
            name: "type",
            type: "custom",
            options: typeData,
            component: SelectSituationScreen,
            componentProps: { typeData, liveSession, dateTime },
            defaultValue: "",
            validationRules: { ...validation.required() },
        },
        {
            is_masked: !isIndividual,
            description: (
                <>
                    {genericDescription(formValues?.type === "one_occurrence")}
                    <TitleM style={{ marginTop: 10 }} fontWeight={"ExtraBold"}>
                        {formValues?.type === "one_occurrence"
                            ? "Déplacer cette session au :"
                            : "Nouveau créneau régulier"}
                    </TitleM>
                </>
            ),
            name: "new_date",
            type:
                formValues?.type === "one_occurrence"
                    ? "date-time"
                    : "day-time",
            defaultValue:
                formValues?.type === "one_occurrence"
                    ? liveMoment?.format("YYYY-MM-DD HH:mm")
                    : liveSession?.day_time,
            range: { startDate: moment().startOf("day").toDate() },
            validationRules: { ...validation.required() },
        },
        {
            label: "Je vais être en retard de",
            is_masked: formValues?.type === "absence" || isIndividual,
            description: <>{genericDescription()}</>,
            name: "late_minutes",
            type: "custom",
            component: SelectDelayComponent,
            componentProps: { delayOptions },
            defaultValue: "",
            bottomComponent: (
                <TextM>
                    Au-delà de 15 minutes de retard, il faut signaler une
                    absence immédiatement.
                </TextM>
            ),
            validationRules: { ...validation.required() },
        },
        {
            is_masked:
                (formValues?.type !== "absence" &&
                    formValues?.late_minutes !== "more") ||
                isIndividual ||
                !absenceSummary,
            description: (
                <>
                    <TitleM fontWeight={"ExtraBold"}>
                        Confirme ton absence
                    </TitleM>
                </>
            ),
            name: "other_occurrences",
            type: "select.multiple",
            options: otherOccurrencesList
                .filter((occ) => {
                    const currentAbsenceDate =
                        dateTime ?? liveMoment?.format("YYYY-MM-DD HH:mm")
                    return (
                        occ.occurrenceDate === currentAbsenceDate &&
                        occ.liveSessionId === liveId
                    )
                })
                .map((occ) => {
                    const occMoment = moment(occ.occurrenceDate)
                    const isToday = occMoment.isSame(moment(), "day")
                    const formattedDate = capitalize(
                        occMoment.format(
                            (isToday ? "[aujourd'hui]" : "dddd") +
                                " DD MMM [à] HH:mm"
                        )
                    )
                    const occValue = `${occ.occurrenceDate}_${occ.liveSessionId}`
                    return {
                        label: formattedDate,
                        value: occValue,
                        description: occ.studentsListString,
                        disabled: true,
                    }
                }),
            defaultValue: [
                `${
                    dateTime ?? liveMoment?.format("YYYY-MM-DD HH:mm")
                }_${liveId}`,
            ],
            // Sous la carte de la séance, pas collé en bas de l'écran.
            bottomComponentInline: true,
            bottomComponent: (
                <>
                    <AbsenceImpactComponent
                        students={absenceImpact}
                        width={cardWidth}
                    />
                    {(() => {
                        if (!absenceSummary) return null

                        const exceedsLimit =
                            absenceSummary.total_days + dynamicAbsenceDays >
                            absenceSummary.limit
                        const hasNextMonthAbsences =
                            absenceSummary.next_month &&
                            absenceSummary.next_month.total_days +
                                dynamicNextMonthDays >
                                0

                        const nextMonthTotal =
                            (absenceSummary.next_month?.total_days || 0) +
                            dynamicNextMonthDays
                        const nextMonthExceedsLimit =
                            nextMonthTotal > absenceSummary.limit

                        const hasNewNextMonthDates = dynamicNextMonthDays > 0
                        const nextMonthVerb = hasNewNextMonthDates
                            ? "auras aussi épuisé"
                            : "as déjà épuisé"

                        const nextMonthNote = hasNextMonthAbsences ? (
                            <TextM
                                wrap
                                style={{
                                    marginTop: 8,
                                    lineHeight: 14,
                                }}
                            >
                                {nextMonthExceedsLimit ? (
                                    <>
                                        Tu {nextMonthVerb}{" "}
                                        <TextM fontWeight={"SemiBold"}>
                                            {nextMonthTotal}{" "}
                                            {plr("crédit", nextMonthTotal)}
                                        </TextM>{" "}
                                        d'absence le mois prochain.
                                    </>
                                ) : (
                                    <>
                                        Tu {nextMonthVerb}{" "}
                                        <TextM fontWeight={"SemiBold"}>
                                            {nextMonthTotal}{" "}
                                            {plr("crédit", nextMonthTotal)}
                                        </TextM>{" "}
                                        d'absence sur les{" "}
                                        <TextM fontWeight={"SemiBold"}>
                                            {absenceSummary.limit} autorisés
                                        </TextM>{" "}
                                        le mois prochain.
                                    </>
                                )}
                            </TextM>
                        ) : null

                        // Sous le bloc d'impact, pas une seconde carte d'alerte :
                        // le décompte des crédits redevient une ligne discrète.
                        if (hasTutorChange(absenceImpact)) {
                            const totalDays =
                                absenceSummary.total_days + dynamicAbsenceDays
                            const charter = (
                                <LinkTextM url="https://masteur.com/charte-dengagement">
                                    charte
                                </LinkTextM>
                            )

                            return (
                                <View
                                    style={{
                                        width: cardWidth,
                                        alignSelf: "flex-start",
                                        marginBottom: 4,
                                    }}
                                >
                                    {emptiesSession(absenceImpact) ? (
                                        <TextM wrap fontSize={12} color={theme.colors.greyAlt}>
                                            Aucun crédit d'absence décompté : il
                                            ne reste plus d'élève à remplacer.
                                        </TextM>
                                    ) : exceedsLimit ? (
                                        <TextM
                                            wrap
                                            fontSize={12}
                                            color={theme.colors.error}
                                            style={{ lineHeight: 16 }}
                                        >
                                            {`Absences ${
                                                absenceSummary.is_new_tutor
                                                    ? "durant ton premier mois"
                                                    : "ce mois-ci"
                                            } : ${totalDays} ${plr("jour", totalDays)}, au-delà de la limite — tu risques de perdre tes séances (`}
                                            {charter}).
                                        </TextM>
                                    ) : (
                                        <TextM
                                            wrap
                                            fontSize={12}
                                            color={theme.colors.greyAlt}
                                            style={{ lineHeight: 16 }}
                                        >
                                            {`Crédits d'absence ce mois-ci : ${totalDays} sur ${absenceSummary.limit}. Absences répétées ou tardives : frais de gestion (`}
                                            {charter}).
                                        </TextM>
                                    )}
                                    {nextMonthNote}
                                </View>
                            )
                        }

                        if (exceedsLimit) {
                            // ATTENTION warning - limit exceeded
                            return importantWarning(
                                <>
                                    <TextM
                                        wrap
                                        style={{
                                            marginBottom: 4,
                                            lineHeight: 14,
                                        }}
                                    >
                                        En confirmant, tu auras été absent{" "}
                                        <TextM fontWeight={"SemiBold"}>
                                            {absenceSummary.total_days +
                                                dynamicAbsenceDays}{" "}
                                            {plr(
                                                "jour",
                                                absenceSummary.total_days +
                                                    dynamicAbsenceDays
                                            )}
                                        </TextM>{" "}
                                        {absenceSummary.is_new_tutor
                                            ? "durant ton premier mois"
                                            : "du mois en cours"}
                                        . Tu risques de perdre tes séances
                                        conformément à la{" "}
                                        <LinkTextM url="https://masteur.com/charte-dengagement">
                                            charte d'engagement
                                        </LinkTextM>
                                        .
                                    </TextM>
                                    {nextMonthNote}
                                </>,
                                {
                                    marginTop: 4,
                                    marginBottom: 4,
                                    width: cardWidth,
                                    alignSelf: "flex-start",
                                },
                                "ATTENTION"
                            )
                        } else {
                            // IMPORTANT warning - limit not exceeded
                            return importantWarning(
                                <>
                                    <TextM
                                        wrap
                                        style={{
                                            lineHeight: 18,
                                            marginBottom: 12,
                                        }}
                                    >
                                        <TextM fontWeight={"SemiBold"}>
                                            Préviens au moins 24h à l'avance{" "}
                                        </TextM>
                                        pour faciliter l'organisation de ton
                                        remplacement.
                                    </TextM>
                                    <TextM
                                        wrap
                                        style={{
                                            lineHeight: 14,
                                        }}
                                    >
                                        En confirmant, tu auras épuisé{" "}
                                        <TextM fontWeight={"SemiBold"}>
                                            {absenceSummary.total_days +
                                                dynamicAbsenceDays}{" "}
                                            {plr(
                                                "crédit",
                                                absenceSummary.total_days +
                                                    dynamicAbsenceDays
                                            )}
                                        </TextM>{" "}
                                        d'absence sur les{" "}
                                        <TextM fontWeight={"SemiBold"}>
                                            {absenceSummary.limit} autorisés
                                        </TextM>{" "}
                                        du mois en cours. Des absences répétées
                                        ou tardives entraîneront des frais de
                                        gestion conformément à la{" "}
                                        <LinkTextM url="https://masteur.com/charte-dengagement">
                                            charte d'engagement
                                        </LinkTextM>
                                        .
                                    </TextM>
                                    {nextMonthNote}
                                </>,
                                {
                                    marginTop: 4,
                                    marginBottom: 4,
                                    width: cardWidth,
                                    alignSelf: "flex-start",
                                },
                                "IMPORTANT"
                            )
                        }
                    })()}
                    <Checkbox.Item
                        label={
                            absenceSummary?.exceeds_limit
                                ? "J'accepte de perdre mes lives"
                                : "Je confirme ma demande d'absence"
                        }
                        position={"leading"}
                        onPress={() => {
                            setAcceptsLiveLoss(!acceptsLiveLoss)
                        }}
                        color={theme.colors.primary}
                        labelStyle={{
                            fontSize: 14,
                            textAlign: "left",
                        }}
                        style={{}}
                        status={acceptsLiveLoss ? "checked" : "indeterminate"}
                    />
                </>
            ),
            showNextButton: acceptsLiveLoss,
            validationRules: {
                ...validation.customCondition(
                    () => acceptsLiveLoss,
                    "Tu dois cocher cette case pour confirmer"
                ),
            },
        },
        {
            label: null,
            description: (
                <>
                    {genericDescription()}
                    <TitleM
                        style={{ marginTop: 10, marginBottom: 0 }}
                        fontWeight={"ExtraBold"}
                    >
                        Souhaites-tu proposer à tes élèves de rattraper les
                        cours sur tes autres créneaux ?
                    </TitleM>
                    <TextM
                        style={{ marginTop: 10, marginBottom: 10 }}
                        color={theme.colors.grey}
                        wrap
                    >
                        {isTooCloseToSession
                            ? "Tu ne peux pas proposer de séances de rattrapage moins de 30 minutes avant le début de la session."
                            : "Sélectionne les séances disponibles, ou passe cette étape pour ne pas proposer de rattrapage."}
                    </TextM>
                </>
            ),
            is_masked:
                formValues?.type !== "absence" ||
                isIndividual ||
                shouldSkipMakeupSteps,
            name: "selected_makeup_occurrences",
            type: "select.multiple",
            options: processedMakeupOccurrences.map((occ) => {
                const occMoment = moment(occ.occurrenceDate)
                const isToday = occMoment.isSame(moment(), "day")
                const formattedDate = capitalize(
                    occMoment.format(
                        (isToday ? "[aujourd'hui]" : "dddd") +
                            " DD MMM [à] HH:mm"
                    )
                )
                return {
                    label: formattedDate,
                    value: `${occ.occurrenceDate}|${occ.liveSessionId}`,
                    description:
                        occ.liveSession.students_list_string === ""
                            ? "(aucun élève)"
                            : occ.liveSession.students_list_string,
                }
            }),
            defaultValue: [],
            validationRules: {},
            showNextButton: true,
        },
        {
            label: null,
            description: (
                <>
                    <TitleM
                        style={{ marginTop: 10 }}
                        fontWeight={"ExtraBold"}
                    >{`Pour confirmer ${
                        formValues?.type !== "absence"
                            ? "le déplacement"
                            : "la demande d'absence"
                    }, indique la raison`}</TitleM>
                </>
            ),
            is_masked:
                formValues?.late_minutes !== "more" &&
                formValues?.type !== "absence" &&
                !isIndividual,
            name: "reason",
            type: "custom",
            component: MoveSessionReasonComponent,
            componentProps: { liveSession, shouldSkipMakeupSteps, dateTime },
            defaultValue: isIndividual ? "" : "Autre raison (préciser)",
            validationRules: isIndividual ? { ...validation.required() } : {},
        },
        {
            is_masked:
                formValues?.late_minutes === "more" ||
                formValues?.type === "absence" ||
                isIndividual,
            description: (
                <>
                    <TitleM
                        style={{ marginTop: 10 }}
                        fontWeight={"ExtraBold"}
                    >{`Signaler un retard`}</TitleM>
                </>
            ),
            name: "reason",
            type: "custom",
            component: MoveSessionReasonComponent,
            componentProps: { liveSession, shouldSkipMakeupSteps, dateTime },
            defaultValue: "",
            validationRules: { ...validation.required() },
        },
    ]

    const successCongrats = () => {
        const congrats = [
            {
                congratsType: congratsTypes.GENERIC,
                congratsData: {
                    title: "Effectué",
                    text:
                        formValues?.late_minutes !== "more" && !isIndividual
                            ? "Merci de vérifier que chaque élève a des activités pertinentes à faire en attendant ton arrivée.\n Il faudra compenser ce retard en fin de séance !"
                            : "Opération réussie !",
                },
            },
        ]
        navigation.replace("congrats", { congrats })
    }

    useApiStatus(liveService.moveSession, false, false, () => {
        props.apiRequest(authService.getUser)
        successCongrats()
    })

    useApiStatus(
        liveService.reportAbsence,
        false,
        false,
        () => {
            props.apiRequest(authService.getUser)
            successCongrats()
        },
        (error) => {
            // L'impact a changé depuis l'aperçu (un élève de plus change de
            // tuteur) : on montre le nouvel impact, le tuteur reconfirme.
            if (error?.absence_impact) {
                setAbsenceImpact(error.absence_impact)
                DialogM.show({
                    text1: "L'impact de ton absence a changé",
                    text2: tutorChangeNotice(error.absence_impact),
                    buttonText1: "J'ai compris",
                    onPress: () => {},
                    exitButton: true,
                    onPressBack: () => {},
                })
                return
            }
            if (error?.has_hypercare_students) {
                DialogM.show({
                    text1: "Ta présence compte",
                    text2: error?.message || "Tu as un élève en premier mois dans cette session.",
                    buttonText1: "Contacter sur WhatsApp",
                    onPress: () => {
                        const url = error?.support_whatsapp_url || props.supportWhatsappUrl
                        if (url) {
                            Linking.openURL(url).catch(() => {})
                        }
                    },
                    buttonText2: "Fermer",
                    onPressBack: () => {},
                })
            }
        }
    )

    const onSubmit = (data) => {
        if (props.moveSessionRequest || props.reportAbsenceRequest) return
        const previousDate = dateTime ?? liveMoment.format("YYYY-MM-DD HH:mm")

        // Convert selected date strings back to full occurrence objects
        const convertToOccurrenceObjects = (selectedKeys) => {
            if (!selectedKeys || selectedKeys.length === 0) return []

            return selectedKeys
                .map((key) => {
                    // Parse the key format: "YYYY-MM-DD HH:mm_liveSessionId"
                    const [occurrenceDate, liveSessionId] = key.split("_")

                    // Find the full occurrence object from the list
                    return otherOccurrencesList.find(
                        (occ) =>
                            occ.occurrenceDate === occurrenceDate &&
                            occ.liveSessionId === parseInt(liveSessionId)
                    )
                })
                .filter((occ) => occ !== undefined) // Remove any undefined entries
                .filter((occ) => occ.occurrenceDate !== previousDate) // Filter out current absence to avoid duplicates
        }

        // Convert selected makeup dates to full occurrence objects
        const convertMakeupOccurrenceObjects = (selectedDates) => {
            if (!selectedDates || selectedDates.length === 0) return []

            return selectedDates
                .map((compositeKey) => {
                    // Parse the composite key "date|sessionId"
                    const [date, sessionId] = compositeKey.split("|")
                    // Find the full occurrence object from the makeup list
                    return processedMakeupOccurrences.find(
                        (occ) =>
                            occ.occurrenceDate === date &&
                            occ.liveSessionId === parseInt(sessionId)
                    )
                })
                .filter((occ) => occ !== undefined) // Remove any undefined entries
        }

        // A delay beyond 15 minutes is declared as an absence.
        const absenceData =
            data?.type !== "absence" && formValues?.late_minutes !== "more"
                ? {
                      type: "delay",
                      late_minutes: data?.late_minutes,
                  }
                : {
                      ...data,
                      type: "absence",
                      late_minutes: undefined,
                      acknowledged_tutor_change_ids: tutorChangeIds(absenceImpact),
                      selected_makeup_occurrences: shouldSkipMakeupSteps
                          ? []
                          : convertMakeupOccurrenceObjects(
                                data?.selected_makeup_occurrences
                            ),
                      other_occurrences: convertToOccurrenceObjects(
                          data?.other_occurrences
                      ),
                  }

        if (isIndividual) {
            props.apiRequest(
                liveService.moveSession,
                { id: liveId },
                formValues?.type === "one_occurrence"
                    ? {
                          ...data,
                          previous_date: previousDate,
                      }
                    : data
            )
        } else {
            props.apiRequest(
                liveService.reportAbsence,
                { id: liveId },
                absenceData
            )
        }
    }

    return (
        <ScreenSkeleton isLoading={props.getSubstitutionAbsenceSummaryRequest}>
            <View
                style={{
                    flex: 1,
                    alignItems: "center",
                }}
            >
                <FormStepper
                    navigation={navigation}
                    track
                    formData={formData?.filter((x) => !x.is_masked)}
                    champion={null}
                    setFormValues={setFormValues}
                    defaultValues={defaultValues}
                    onSubmit={onSubmit}
                    submitted={
                        props.moveSessionRequest || props.reportAbsenceRequest
                    }
                    previousRouteName={"menu"}
                    submittedMessage=""
                    serverError={
                        props.moveSessionError || props.reportAbsenceError
                    }
                />
            </View>
        </ScreenSkeleton>
    )
}

const mapStateToProps = (state) => {
    return {
        user: state.data.currentUser?.object,
        getUserRequest: state.api.getUser?.request,
        schoolSubjects: state.data.staticData?.school_subjects,
        moveSessionRequest: state.api.moveSession?.request,
        moveSessionError: state.api.moveSession?.error,
        moveSessionSuccessData: state.api.moveSession?.data,
        reportAbsenceRequest: state.api.reportAbsence?.request,
        reportAbsenceError: state.api.reportAbsence?.error,
        supportWhatsappUrl: state.data.staticData?.support_whatsapp_url,
        getSubstitutionAbsenceSummaryRequest:
            state.api.getSubstitutionAbsenceSummary?.request,
    }
}

const mapDispatchToProps = {
    apiRequest,
    apiIdle,
}

export default connect(mapStateToProps, mapDispatchToProps)(ManageRDVScreen)
